import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  MailIcon, 
  LockIcon, 
  EyeIcon, 
  EyeOffIcon, 
  SunIcon, 
  MoonIcon,
  CheckIcon,
  UserIcon,
  ZapIcon,
  SecurityIcon,
  LayersIcon
} from '../components/common/Icons';

interface RegisterPageProps {
  onRegister?: (name: string, email: string, password: string) => void;
  onNavigateToLogin?: () => void;
  error?: string | null;
  onErrorClear?: () => void;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ onRegister, onNavigateToLogin, error, onErrorClear }) => {
  const { theme, toggleTheme } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const passwordStrength = () => {
    if (!password) return { level: 0, text: '', color: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { level: strength, text: 'Weak', color: 'var(--error)' };
    if (strength <= 3) return { level: strength, text: 'Medium', color: 'var(--warning)' };
    return { level: strength, text: 'Strong', color: 'var(--success)' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (onRegister) {
      onRegister(name, email, password);
    }
    setIsLoading(false);
  };

  const strength = passwordStrength();

  return (
    <div className="auth-layout">
      {/* Background Effects */}
      <div className="auth-bg-effects">
        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />
      </div>

      {/* Left Panel - Branding */}
      <div className="auth-panel-left">
        <div className="auth-branding">
          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-logo-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
              </svg>
            </div>
            <span className="auth-logo-text">AI Platform</span>
          </div>

          {/* Headline */}
          <h1 className="auth-headline">
            Start Building<br />
            <span className="highlight">Today</span>
          </h1>

          <p className="auth-subheadline">
            Get started with AI Platform's free tier. No credit card required.
            Scale up as you grow.
          </p>

          {/* Features */}
          <div className="auth-features">
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <ZapIcon size={18} />
              </div>
              <span>100k free API calls per month</span>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <SecurityIcon size={18} />
              </div>
              <span>Full access to all features</span>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <LayersIcon size={18} />
              </div>
              <span>Dedicated support channel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Registration Form */}
      <div className="auth-panel-right">
        {/* Theme Toggle */}
        <button 
          className="theme-toggle" 
          onClick={toggleTheme}
          style={{ position: 'absolute', top: '24px', right: '24px' }}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
        </button>

        <div className="auth-card">
          {/* Header */}
          <div className="auth-card-header">
            <h2 className="auth-card-title">Create an account</h2>
            <p className="auth-card-subtitle">Start your 14-day free trial</p>
          </div>

          {/* Auth Error Banner */}
          {error && (
            <div style={{
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px',
              color: '#f87171',
              fontSize: '13px',
              marginBottom: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span>{error}</span>
              <button onClick={onErrorClear} style={{
                background: 'none', border: 'none', color: '#f87171',
                cursor: 'pointer', fontSize: '16px', padding: '0 4px',
              }}>×</button>
            </div>
          )}

          {/* Registration Form */}
          <form className="auth-form" onSubmit={handleSubmit}>
            {/* Name Input */}
            <div className="input-group">
              <label className="input-label" htmlFor="name">Full Name</label>
              <div className="input-wrapper">
                <UserIcon size={18} className="input-icon" />
                <input
                  id="name"
                  type="text"
                  className="input input-with-icon input-lg"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Email Input */}
            <div className="input-group">
              <label className="input-label" htmlFor="email">Work Email</label>
              <div className="input-wrapper">
                <MailIcon size={18} className="input-icon" />
                <input
                  id="email"
                  type="email"
                  className="input input-with-icon input-lg"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="input-group">
              <label className="input-label" htmlFor="password">Password</label>
              <div className="input-wrapper">
                <LockIcon size={18} className="input-icon" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="input input-with-icon input-lg"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: '48px' }}
                />
                <button
                  type="button"
                  className="input-icon-right"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                </button>
              </div>
              {/* Password Strength */}
              {password && (
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '2px',
                          background: i <= strength.level ? strength.color : 'var(--glass-border)'
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: 'var(--text-xs)', color: strength.color }}>
                    {strength.text}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="input-group">
              <label className="input-label" htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <LockIcon size={18} className="input-icon" />
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  className={`input input-with-icon input-lg ${confirmPassword && confirmPassword !== password ? 'error' : ''}`}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {confirmPassword && confirmPassword !== password && (
                <span className="input-helper error">Passwords do not match</span>
              )}
            </div>

            {/* Terms Checkbox */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
              <div 
                onClick={() => setAgreeTerms(!agreeTerms)}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '6px',
                  border: `1px solid ${agreeTerms ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
                  background: agreeTerms ? 'var(--accent-primary)' : 'var(--glass-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  marginTop: '2px'
                }}
              >
                {agreeTerms && <CheckIcon size={14} style={{ color: 'white' }} />}
              </div>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                I agree to the{' '}
                <a href="#terms" style={{ color: 'var(--accent-primary)' }}>Terms of Service</a>
                {' '}and{' '}
                <a href="#privacy" style={{ color: 'var(--accent-primary)' }}>Privacy Policy</a>
              </span>
            </label>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn btn-primary btn-xl"
              disabled={isLoading || !agreeTerms}
              style={{ width: '100%', marginTop: '8px' }}
            >
              {isLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="spinner" style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  Creating account...
                </span>
              ) : (
                'Create account'
              )}
            </button>

            {/* Divider */}
            <div className="auth-divider">
              <span>or continue with</span>
            </div>

            {/* Social Buttons */}
            <div className="auth-social-btns">
              <button type="button" className="auth-social-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Google
              </button>
              <button type="button" className="auth-social-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                GitHub
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="auth-footer">
            Already have an account?{' '}
            <a href="#login" onClick={onNavigateToLogin}>Sign in</a>
          </div>
        </div>
      </div>

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;
